import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, iif, of, Subject } from 'rxjs';
import { debounceTime, delay, map, switchMap, takeUntil } from 'rxjs/operators';
import { Unit } from 'app/models/entities/unit';
import { Membership } from 'app/models/entities/membership';
import { ElasticSearchService } from 'app/services/search/elastic-search.service';
import { GrowlerService } from 'app/services/growler.service';

export interface DropdownSearchItem
{
    index: 'units' | 'members';
    model: Unit | Membership;
}

@UntilDestroy()
@Component({
    selector: 'app-header-search',
    templateUrl: './header-search.component.html',
    styleUrls: ['./header-search.component.scss'],
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatIconModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatDividerModule,
        NgIf,
        NgForOf,
        NgSwitch,
        NgSwitchCase,
        MatInputModule,
    ],
})
export class HeaderSearchComponent implements OnInit
{
    @Output() resultSelected = new EventEmitter<string>();

    results: DropdownSearchItem[] = [];
    control = this._fb.control<string | number>('');
    searchingState$ = new BehaviorSubject<boolean>(false);
    isSearching = false;

    private abortPreviousSearch = new Subject<void>();

    constructor(
            private _fb: FormBuilder,
            private _searchService: ElasticSearchService,
            private _growler: GrowlerService,
    ) {
    }

    get filteredResults(): DropdownSearchItem[] {
        return this.results.filter((item) => {
            const value = this.getValue(item).trim().toLowerCase();
            return value.indexOf(this._searchService.searchString) > -1;
        });
    }

    ngOnInit(): void {
        this.control.valueChanges.pipe(
                debounceTime(500),
                map((value) => {
                    switch(typeof(value)) {
                        case 'number':
                            return value.toString();
                        case 'string':
                            return value.toLowerCase();
                        default:
                            return null;
                    }
                }),
                untilDestroyed(this),
            ).subscribe({
            next: (value) => this.search(value.trim()),
        });

        this.searchingState$.asObservable().pipe(
                switchMap((loading) => iif(() => loading, of(loading), of(loading).pipe(delay(100)))),
                untilDestroyed(this),
        ).subscribe({
            next: (searching) => this.isSearching = searching,
        });
    }

    getValue(result: DropdownSearchItem): string {
        switch(result.index) {
            case 'units':
                return (result.model as Unit).title;
            case 'members':
                return (result.model as Membership).profile.name;
        }
    }

    search(value: string) {
        if (this.isSearching || this._searchService.searchString === value) {
            return;
        }

        this.abortPreviousSearch.next();
        this.searchingState$.next(true);
        this._searchService.search(value, 3).pipe(
                takeUntil(this.abortPreviousSearch),
                untilDestroyed(this),
        ).subscribe({
            next: (hits) => {
                // TODO: ngxs managed search results state?
                this.results = hits.map((hit) => {
                    let model: Unit | Membership;
                    switch (hit.index) {
                        case 'units':
                            model = new Unit(hit);
                            break;
                        case 'members':
                            model = new Membership(hit);
                            break;
                    }
                    return {
                        index: hit.index,
                        model,
                    };
                });
            },
            error: () => this._growler.oops('There was a problem loading search results.'),
            complete: () => this.searchingState$.next(false),
        });
    }
}
